import React, { useState, useEffect } from "react";
import axios from "axios";

const TicketDetailsModal = ({ isOpen, onClose, ticketId }) => {
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && ticketId) {
      fetchTicketDetails(ticketId);
    }
  }, [isOpen, ticketId]);

  const fetchTicketDetails = async (ticketId) => {
    setLoading(true);
    try {
      const [ticketResponse, commentsResponse, sentimentsResponse] =
        await Promise.all([
          axios.get(`/tickets/${ticketId}`),
          axios.get(`/tickets/${ticketId}/comments`),
          axios.get(`/tickets/${ticketId}/sentiments`),
        ]);

      const ticketData = ticketResponse.data;
      const commentsData = commentsResponse.data;
      const sentimentsData = sentimentsResponse.data;

      const ticketDetails = {
        ...ticketData,
        comments: commentsData,
        sentiments: sentimentsData,
      };

      setTicketDetails(ticketDetails);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      setError("Failed to fetch ticket details. Please try again later.");
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTicketDetails(null);
    onClose();
  };

  const handleModalClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Arial', sans-serif",
      }}
      onClick={handleModalClick}
    >
      <div
        className="modal-content"
        style={{
          position: "relative",
          backgroundColor: "#fff",
          padding: 30,
          borderRadius: 10,
          boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.3)",
          maxWidth: "70%",
          maxHeight: "90%",
          overflowY: "auto",
          border: "1px solid #ddd",
        }}
      >
        <span
          className="close"
          onClick={handleClose}
          style={{
            position: "absolute",
            top: 15,
            right: 15,
            cursor: "pointer",
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          &times;
        </span>
        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              fontSize: "1.5rem",
              color: "#333",
            }}
          >
            <i className="fas fa-spinner fa-spin" />
            <p>Loading...</p>
          </div>
        )}
        {error && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              fontSize: "1.5rem",
              color: "red",
            }}
          >
            <i className="fas fa-exclamation-triangle" />
            <p>{error}</p>
          </div>
        )}
        {ticketDetails && (
          <div>
            <h2
              style={{
                borderBottom: "2px solid #eee",
                paddingBottom: 10,
                fontSize: "1.8rem",
                fontWeight: "bold",
                color: "#333",
                marginBottom: 20,
              }}
            >
              <b>Subject: </b>
              {ticketDetails.subject}
            </h2>
            <div
              style={{
                padding: 15,
                border: "1px solid #ddd",
                borderRadius: 10,
                marginBottom: 20,
                backgroundColor: "#f9f9f9",
              }}
            >
              <p
                style={{
                  fontSize: "1.2rem",
                  color: "#333",
                  marginBottom: 5,
                }}
              >
                <strong>Comments:</strong>
              </p>
              <ul
                style={{
                  paddingLeft: 20,
                  listStyle: "none",
                  margin: 0,
                }}
              >
                {ticketDetails.comments.map((comment) => (
                  <li
                    key={comment.id}
                    style={{
                      fontSize: "1rem",
                      color: "#666",
                      marginBottom: 10,
                      paddingBottom: 10,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "0.9rem",
                        color: "#444",
                      }}
                    >
                      {comment.comment_body}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
            {ticketDetails.sentiments.some((s) => s.root_cause_summary) && (
              <div
                style={{
                  padding: 15,
                  border: "1px solid #ddd",
                  borderRadius: 10,
                  marginBottom: 20,
                  backgroundColor: "#fff8f0",
                  borderLeft: "4px solid #e67e22",
                }}
              >
                <h3
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    color: "#333",
                    marginBottom: 10,
                  }}
                >
                  Root Cause Analysis:
                </h3>
                {ticketDetails.sentiments
                  .filter((s) => s.root_cause_summary)
                  .map((sentiment, idx) => (
                    <p
                      key={idx}
                      style={{
                        fontSize: "1rem",
                        color: "#444",
                        lineHeight: "1.6",
                      }}
                    >
                      {sentiment.root_cause_summary}
                    </p>
                  ))}
              </div>
            )}
            <div
              style={{
                padding: 15,
                border: "1px solid #ddd",
                borderRadius: 10,
                backgroundColor: "#f9f9f9",
              }}
            >
              <h3
                style={{
                  fontSize: "1.4rem",
                  fontWeight: "bold",
                  color: "#333",
                  marginBottom: 15,
                }}
              >
                Sentiment Analysis:
              </h3>
              <ul
                style={{
                  paddingLeft: 20,
                  listStyle: "none",
                  margin: 0,
                }}
              >
                {ticketDetails.sentiments.map((sentiment) => (
                  <li
                    key={sentiment.id}
                    style={{
                      marginBottom: 10,
                      paddingBottom: 10,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#444",
                        marginBottom: 5,
                      }}
                    >
                      <strong>Agent Summary:</strong> {sentiment.agent_summary}
                    </p>
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#444",
                        marginBottom: 5,
                      }}
                    >
                      <strong>Agent Sentiment:</strong>{" "}
                      {sentiment.agent_sentiment}
                    </p>
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#444",
                        marginBottom: 5,
                        marginTop: 20,
                      }}
                    >
                      <strong>Customer Summary:</strong>{" "}
                      {sentiment.customer_summary}
                    </p>
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#444",
                        marginBottom: 5,
                      }}
                    >
                      <strong>Customer Sentiment:</strong>{" "}
                      {sentiment.customer_sentiment}
                    </p>
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#444",
                        marginTop: 10,
                      }}
                    >
                      <strong>CSAT Score:</strong> {sentiment.csat_score}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDetailsModal;
